import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { InboxItem, InboxService } from '../../core/services/inbox.service';

@Component({
  selector: 'app-notification-panel',
  templateUrl: './notification-panel.component.html',
})
export class NotificationPanelComponent implements OnChanges, OnDestroy {
  @Input()  isOpen = false;
  @Output() closed = new EventEmitter<void>();

  items:   InboxItem[] = [];
  loading  = false;
  hasError = false;
  marking  = false;

  get unreadCount(): number {
    return this.items.filter(i => !i.isRead).length;
  }

  private sub?: Subscription;
  private readonly keyHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') this.close();
  };

  constructor(
    private readonly inbox:  InboxService,
    private readonly router: Router,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']?.currentValue === true) {
      this.load();
      document.addEventListener('keydown', this.keyHandler);
    } else {
      document.removeEventListener('keydown', this.keyHandler);
    }
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    document.removeEventListener('keydown', this.keyHandler);
  }

  load(): void {
    this.loading  = true;
    this.hasError = false;
    this.sub?.unsubscribe();
    this.sub = this.inbox.load().subscribe({
      next: ({ items }) => {
        this.items   = items;
        this.loading = false;
      },
      error: () => {
        this.loading  = false;
        this.hasError = true;
      },
    });
  }

  close(): void {
    this.closed.emit();
  }

  openItem(item: InboxItem): void {
    if (!item.isRead) {
      item.isRead = true;
      this.inbox.markAsRead(item.findingId).subscribe();
    }
    const path  = this.serviceTypePath(item.serviceType);
    const param = this.serviceTypeParam(item.serviceType);
    this.router.navigate([path], { queryParams: { [param]: item.resourceId } });
    this.close();
  }

  markAllAsRead(): void {
    this.marking = true;
    this.inbox.markAllAsRead().subscribe({
      next: () => {
        this.items   = this.items.map(i => ({ ...i, isRead: true }));
        this.marking = false;
      },
      error: () => {
        this.marking = false;
      },
    });
  }

  iconName(item: InboxItem): string {
    return item.type === 'FINDING_RESOLVED' ? 'circle-check' : 'triangle-alert';
  }

  iconBg(item: InboxItem): string {
    if (item.type === 'FINDING_RESOLVED') return 'bg-emerald-500';
    return item.severity === 'CRITICAL' ? 'bg-red-500' : 'bg-amber-500';
  }

  relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60_000);
    if (mins < 1)  return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24)  return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  private serviceTypePath(serviceType: string): string {
    switch (serviceType) {
      case 'LAMBDA':   return '/cloud/lambda';
      case 'GLUE':     return '/cloud/glue';
      case 'PIPELINE': return '/cloud/codepipeline';
      default:         return '/';
    }
  }

  private serviceTypeParam(serviceType: string): string {
    switch (serviceType) {
      case 'LAMBDA':   return 'fn';
      case 'GLUE':     return 'job';
      case 'PIPELINE': return 'pipeline';
      default:         return 'id';
    }
  }
}
